import _ from 'lodash'
import React, { Fragment, useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'
import { useHistory } from 'react-router-dom'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import { Button, Header, Icon, Modal, Popup, Segment, Table } from 'semantic-ui-react'
import { MenuItemProps } from 'semantic-ui-react/dist/commonjs/collections/Menu/MenuItem'
import floodService from '../../apis/FloodServiceAPI'
import { updateCandidateAddressesData } from '../../common/candidateAddresses.slice'
import { downloadFile } from '../../common/download-utils'
import { acceptDisclaimer, getDisclaimerAccepted, MatchProps, selectParcel } from '../../common/slice'
import { useAppDispatch, useAppSelector } from '../../common/store'
import { replaceXWithSpace } from '../../common/util'
import { FLOOD_SUB_PATH, RECAPTCHA_ENABLED, RIVERINE, STORM_100, STORM_1000, STORM_SURGE } from '../../environment'
import PlanningSearchBar from '../planning-components/PlanningSearchBar'
import { resetPlanningSearchValue } from '../planning-components/slice'
import VctRecaptcha from '../vct-recaptcha/VctRecaptcha'
import FloodImpactTab from './FloodImpactTab'
import './FloodMainComponent.css'
import { getSelectedFloodLayer, getSelectedTab, selectFloodParcel, switchFloodLayer, switchFloodTab } from './slice'
import Toggle from './Toggle'
import { numberFormatter, useFloodData } from './util'


function FloodMainComponent({match}: MatchProps) {
    const dispatch = useAppDispatch()
    const laisKey = match?.params?.laisKey
    const disclaimerAccepted = useAppSelector(getDisclaimerAccepted)
    const [cookies, setCookie] = useCookies(['disclaimerAccepted'])
    const floodData = useFloodData()
    const riverineTableData = floodData?.riverineData
    const stormSurgeTableData = floodData?.stormSurgeData
    const [floodDataLoaded, setFloodDataLoaded] = useState(false)
    const [pdfLoading, setPdfLoading] = useState(false)
    const [pdfFailed, setPdfFailed] = useState(false)
    const hasFloodStudy = (riverineTableData && riverineTableData.length > 0) || (stormSurgeTableData && stormSurgeTableData.length > 0)
    const activeTab = useAppSelector(getSelectedTab)
    const activeFloodLayer = useAppSelector(getSelectedFloodLayer)
    const history=useHistory()

    const showRiverineData = React.useMemo(() => {
        if (riverineTableData && riverineTableData.length > 0) {
            for (const [key, value] of Object.entries(riverineTableData[0].floodDetails)) {
                if (typeof value === 'number') {
                    if (numberFormatter.format(value) !== '0.00') {
                        return true
                    }
                }
            }
        }
        return false
    }, [riverineTableData])

    const showStormData = React.useMemo(() => {
        if (stormSurgeTableData && stormSurgeTableData.length > 0) {
            for (let i = 0; i < stormSurgeTableData.length; i++) {
                for (const [key, value] of Object.entries(stormSurgeTableData[i].floodDetails)) {
                    if (typeof value === 'number') {
                        if (numberFormatter.format(value) !== '0.00') {
                            return true
                        }
                    }
                }
            }
        }
        return false
    }, [stormSurgeTableData])

    useEffect(() => {
        if (cookies?.disclaimerAccepted) {
            setDisclaimerAccepted()
        }
        if (laisKey && (_.isEmpty(floodData))) {
            dispatch(selectFloodParcel({'laisKey': replaceXWithSpace(laisKey)}))
        }
        setFloodDataLoaded(true)
    }, [laisKey])


    useEffect(() => {
        if (showRiverineData && !showStormData) {
            if (activeTab === STORM_SURGE) {
                dispatch(switchFloodTab(RIVERINE))
            }
        } else if (showStormData && !showRiverineData) {
            if (activeTab === RIVERINE) {
                dispatch(switchFloodTab(STORM_SURGE))
            }
        }
    }, [floodData])


    function setDisclaimerAccepted() {
        dispatch(acceptDisclaimer())
    }

    const acceptClicked = () => {
        setCookie('disclaimerAccepted', true, {path: '/'}) // setting the cookie
        setDisclaimerAccepted()
    }


    async function handlePrintButtonClick() {
        setPdfLoading(true)
        try {
            const res = await floodService.getFloodInfoPdfApi(laisKey)
            const blob = new Blob([res.data], {type: 'application/pdf'})
            const fileName = res.headers['content-disposition'].split('filename=')[1]
            downloadFile(blob, fileName, 'pdf', {mimeType: 'application/pdf'})
            setPdfFailed(false)
        } catch (error) {
            setPdfFailed(true)
        } finally {
            setPdfLoading(false)
        }
    }


    /**
     * Returns a key to identify this component. This is used by the react CSSTransition to compare keys when re-rendering the component.
     * @returns {string|*}
     */
    function getPlanningDataKey() {
        if (!floodData || _.isEmpty(floodData)) {
            return 'empty'
        } else {
            return floodData.address
        }
    }

    const generateAddressHeader = () => {
        return (
            <Header as='h3' attached='top' className={'floodDetailsHeader'}>
                {floodData?.address ? floodData.address : 'UNAVAILABLE'}
            </Header>
        )
    }

    const displayNoValueMessage = () => {
        return (
            <Segment attached className={'floodDetailsSegment'}>
                <Header className={'errorMessage'}>
                    There are no flood levels for this parcel
                </Header>
            </Segment>
        )
    }

    const displayFloodInformationTable = () => {
        return (
            <Segment attached className={'floodDetailsSegment'}>
                <FloodImpactTab
                    riverineData={riverineTableData}
                    stormSurgeData={stormSurgeTableData}
                    showRiverineData={showRiverineData}
                    showStormData={showStormData}
                    activeTab={activeTab}
                    onTabClick={(e: React.MouseEvent<HTMLAnchorElement>, {name}: MenuItemProps) => dispatch(switchFloodTab(name ? name : RIVERINE))}
                />
            </Segment>
        )
    }

    const onClickSearchClear = () => {
        dispatch(updateCandidateAddressesData([]))
        dispatch(resetPlanningSearchValue())
        dispatch(selectParcel(''))
        history.push(FLOOD_SUB_PATH)
    }

    const displayNoFloodInfoErrorMessage = () => {
        return (
            <Segment attached className={'floodDetailsSegment'}>
                <Header className={'errorMessage'}>
                    There are no applicable flood studies for this parcel
                </Header>
            </Segment>
        )
    }


    const generatePrintButton = () => {
        return (
            <div className={'floodImpactPrintButton'}>
                <Segment attached='bottom'>
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        <Button icon labelPosition='left'
                                className="printContainer"
                                onClick={handlePrintButtonClick}
                                loading={pdfLoading}>
                            <Icon name='print'/>
                            Print Report
                        </Button>
                        {displayDefinitionPopup()}
                        {(activeTab === STORM_SURGE) && RenderStormSwitch()}
                        {pdfFailed && <p style={{color: 'red'}}>Failed to fetch the PDF file</p>}
                    </div>
                    <div className={'floodCurrentLayerMsg'}>
                        {(activeTab === RIVERINE && (showStormData && !(showRiverineData))) &&
                            <p style={{color: 'red'}}>Currently showing Riverine</p>}
                        {(activeTab === STORM_SURGE && (showRiverineData && !(showStormData))) &&
                            <p style={{color: 'red'}}>Currently showing Storm Surge</p>}
                    </div>
                </Segment>
            </div>
        )
    }

    const RenderStormSwitch = () => {
        return (
            <div className={'switchContainer'}>
                Flood Overlay:
                <div style={{display: 'flex'}}>
                    <Toggle label={'100y'} isToggleOn={!(activeFloodLayer === STORM_100)} onClick={(value) => {
                        if (activeTab === RIVERINE) {
                            dispatch(switchFloodTab(STORM_SURGE))
                        }
                        if (value) {
                            dispatch(switchFloodLayer(STORM_1000))
                        } else {
                            dispatch(switchFloodLayer(STORM_100))
                        }
                    }}/>
                    1000 years
                </div>
            </div>
        )
    }

    const displayDefinitionPopup = () => {
        return (
            <Modal
                closeIcon={<Icon size={'small'} color={'black'} name={'close'}/>}
                trigger={
                    <div style={{display: 'flex', marginTop: '15px', marginLeft: '15px'}}>
                        <p className={'usefulDefinitions'}>Useful Definitions</p>
                        <Icon style={{marginLeft: '5px'}} size={'small'} color={'blue'} name={'info'} link circular/>
                    </div>
                }>
                <Modal.Header className={'definitionHeader'}>Useful Definitions</Modal.Header>
                <Modal.Content>
                    <Table striped>
                        <Table.Body>
                            <Table.Row>
                                <Table.Cell>AEP</Table.Cell>
                                <Table.Cell>Annual Exceedence Probability. An AEP of 1% has a 1 in 100 likelihood of
                                    occurring in any given year.</Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell>ARI</Table.Cell>
                                <Table.Cell>Annual Recurrence Interval. An ARI is the average period between exceedences
                                    of a given rainfall.</Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell>mAHD</Table.Cell>
                                <Table.Cell>Metres Australia Height Datum is the reference for defining ground levels in
                                    Australia. AHD 0.0m is approximately mean sea level.</Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell>Water Depth (m)</Table.Cell>
                                <Table.Cell>Water depth is calculated based on ground level information (topography)
                                    that is available to the Department
                                    at the time of the study. It may not take into account most recent changes. An
                                    accurate ground level for your property will need
                                    to be obtained by a qualified surveyor.</Table.Cell>
                            </Table.Row>
                        </Table.Body>
                    </Table>
                </Modal.Content>
            </Modal>
        )
    }

    const displayLoadingIcon = () => {
        return (
            <Segment attached className={'detailsSegment'}>
                <Icon loading name="spinner" size="big"/>
            </Segment>
        )
    }

    const renderFloodDetails = () => {
        const showFloodData = showRiverineData || showStormData
        return (
            <TransitionGroup className={'floodDetailsTransitionContainer'}>
                <CSSTransition
                    key={getPlanningDataKey()}
                    appear={true}
                    in={true}
                    timeout={1000}
                    classNames="flood-details">
                    <div className={'floodDetailsContainer'}>
                        <Popup content="Clear" size="mini"
                               trigger={<Icon className={'searchClearIcon'} size="large" style={{color: '#959d9f'}} link
                                              name="close" onClick={onClickSearchClear}/>}/>
                        <div className={'floodDetails'}>
                            {generateAddressHeader()}
                            <Segment attached>
                                <p className={'planningDetailsLocationAndLot'}>
                                    {floodData?.lotDetails ? floodData.lotDetails : 'UNAVAILABLE'}
                                </p>
                            </Segment>
                            {!floodDataLoaded ? displayLoadingIcon() : hasFloodStudy ? showFloodData ?
                                displayFloodInformationTable() : displayNoValueMessage() : displayNoFloodInfoErrorMessage()}
                            {floodDataLoaded && hasFloodStudy && showFloodData ? generatePrintButton() : null}
                        </div>
                    </div>
                </CSSTransition>
            </TransitionGroup>
        )
    }

    return (
        <Fragment>
            <PlanningSearchBar urlSubPath={FLOOD_SUB_PATH}/>
            {RECAPTCHA_ENABLED && <VctRecaptcha/>}
            <Modal
                open={!disclaimerAccepted}
            >
                <Modal.Header>Disclaimer</Modal.Header>
                <Modal.Content>
                    <p>
                        The flood impact information provided on this website is calculated using a design flood event
                        that has a statistical chance of occurring in any given year (i.e.
                        1% AEP has a 1 per cent chance of occurring in any given year) and is based on flood studies
                        that approximate real events.
                    </p>
                    <p>
                        While care has been taken to ensure that information contained on this website is true and
                        correct at the time of publication, it is your responsibility to independently verify the
                        information provided. We recommend you obtain advice from an appropriately qualified
                        professional. It should also be noted that flood level information reported against units and/or
                        apartments under a unit title scheme or unit parcel scheme are derived from the parent (or base)
                        parcel of the unit development.
                    </p>
                    <p>
                        The Territory gives no warranty or assurance as to the accuracy of the information contained
                        on this website and accepts no direct or indirect liability for reliance on its contents.
                        This includes, but is not limited to, any personal injury, loss of life, or damage to
                        property in any flooding event.
                    </p>
                    <p>
                        By accepting this disclaimer, you are also accepting the <a id="terms" target="_blank"
                                                                                    href="https://nt.gov.au/page/copyright-disclaimer-and-privacy"><u>Terms
                        of Use</u>
                    </a> that apply to this and
                        all Northern Territory Government websites.
                    </p>
                </Modal.Content>
                <Modal.Actions>
                    <Button color="green" onClick={acceptClicked}>Accept</Button>
                </Modal.Actions>
            </Modal>
            {!_.isEmpty(floodData) && renderFloodDetails()}
        </Fragment>

    )


}

export default FloodMainComponent